import { List } from '@elipssolution/harfang';
import { useTheme } from '@mui/material';
import { useCallback, useMemo } from 'react';

import SimulatorItem from './components/SimulatorItem';
import { SimulatorDetailsType } from './types/simulator';
import { WidgetSimulatorsContext } from './utils/context';
import { getSimulatorDetails } from './utils/simulatorDetailsUtil';
import { WidgetExpandedComponentProps, WidgetSimulatorsConfigurationType } from '../../../types/widget';
import { RenderModeEnum } from '../../../utils/widgets';
import { useSession } from '../../components/session/SessionContext';
import { UserTypeEnum } from '../../types/user';

const LIST_ROW_HEIGHT = 52;

const widgetContextValue = { renderMode: RenderModeEnum.LIST };

const ExpandedWidgetSimulators = ({
	configuration,
}: WidgetExpandedComponentProps<WidgetSimulatorsConfigurationType>) => {
	const { shape } = useTheme();
	const {
		user: { type: userType },
	} = useSession();

	const simulators = useMemo(
		() =>
			(configuration?.simulators ?? [])
				.sort((a, b) => a.name.localeCompare(b.name))
				.reduce(
					(acc, simulator) =>
						(userType === UserTypeEnum.INTERNAL && !simulator.isInternal) ||
						(userType === UserTypeEnum.EXTERNAL && !simulator.isExternal) ||
						!simulator.isAvailableOnMobileFormat
							? acc
							: ([...acc, getSimulatorDetails({ simulator })] as SimulatorDetailsType[]),
					[] as SimulatorDetailsType[],
				),
		[configuration, userType],
	);

	const listStyle = {
		height:
			simulators.length === 0
				? '100%'
				: // Height of every row + their bottom Divider, and the last row doesn't have a bottom Divider
				  `calc((${LIST_ROW_HEIGHT}px * ${simulators.length}) + ${simulators.length}px - 1px)`,
		borderRadius: shape.borderRadius * 2,
		overflow: 'hidden',
	};

	const dataSource = useCallback(
		() =>
			Promise.resolve({
				items: simulators,
				count: simulators.length,
			}),
		[simulators],
	);

	const rowRenderer = ({ backgroundColor, icon, name }: SimulatorDetailsType) => (
		<SimulatorItem name={name} backgroundColor={backgroundColor} icon={icon} />
	);

	const handleRowClick = ({ url }: SimulatorDetailsType) => window.open(url, '_blank');

	return (
		<WidgetSimulatorsContext.Provider value={widgetContextValue}>
			<List style={listStyle} dataSource={dataSource} rowRenderer={rowRenderer} onRowClick={handleRowClick} />
		</WidgetSimulatorsContext.Provider>
	);
};

export default ExpandedWidgetSimulators;
